// Editor rico do laudo avulso. Entrega HTML, que e exatamente o que o
// gerador de PDF do ERP consome — por isso o conteudo escrito aqui cai na
// mesma folha dos outros laudos, sem um segundo caminho de geracao.
import React, { useRef } from 'react';
import { Button, Select, Space, Tooltip } from 'antd';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Imagem from '@tiptap/extension-image';
import Tabela from '@tiptap/extension-table';
import TabelaLinha from '@tiptap/extension-table-row';
import TabelaCelula from '@tiptap/extension-table-cell';
import TabelaCabecalho from '@tiptap/extension-table-header';

const SIMBOLOS = ['°', '±', '≤', '≥', '×', '÷', '²', '³', 'Ø', 'µ', 'Ω',
  'α', 'β', 'Δ', 'π', '√', '→', '✓', '✗', '–'];

// A imagem entra no laudo como copia reduzida ao tamanho de impressao, igual
// ao que as fotos dos outros laudos recebem. Sem isso uma foto de celular de
// 6 MB iria inteira para dentro do documento.
const comprimir = (arquivo, max, q) => new Promise((resolve, reject) => {
  const fr = new FileReader();
  fr.onerror = reject;
  fr.onload = () => {
    const img = new window.Image();
    img.onerror = reject;
    img.onload = () => {
      const e = Math.min(1, max / Math.max(img.width, img.height));
      const c = document.createElement('canvas');
      c.width = Math.round(img.width * e);
      c.height = Math.round(img.height * e);
      const ctx = c.getContext('2d');
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL('image/jpeg', q));
    };
    img.src = fr.result;
  };
  fr.readAsDataURL(arquivo);
});

export default function EditorLaudo({ conteudoInicial, aoCriar, bloqueado }) {
  const arqRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ horizontalRule: { HTMLAttributes: { class: 'quebra' } } }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Imagem.configure({ inline: false, allowBase64: true }),
      Tabela.configure({ resizable: true }),
      TabelaLinha, TabelaCelula, TabelaCabecalho
    ],
    content: conteudoInicial || '<p></p>',
    editable: !bloqueado,
    onCreate: ({ editor: ed }) => { if (aoCriar) aoCriar(ed); }
  });

  if (!editor) return null;

  const cmd = (fn) => () => fn(editor.chain().focus()).run();
  const ativo = (n, a) => (editor.isActive(n, a) ? 'primary' : 'default');
  const naTabela = editor.isActive('table');

  const escolherImagem = async (ev) => {
    const arq = ev.target.files && ev.target.files[0];
    ev.target.value = '';
    if (!arq) return;
    try {
      const src = await comprimir(arq, 1600, 0.82);
      editor.chain().focus().setImage({ src }).run();
    } catch (e) { /* imagem invalida: nao insere nada */ }
  };

  const bt = { size: 'small' };

  return (
    <div>
      <style>{`
        .ed-barra { background:#fafafa; border:1px solid #d9d9d9; border-bottom:0;
          border-radius:6px 6px 0 0; padding:6px 8px; display:flex; flex-wrap:wrap; gap:6px; }
        .ed-caixa { border:1px solid #d9d9d9; border-radius:0 0 6px 6px; background:#fff; }
        .ed-caixa .ProseMirror { min-height:420px; padding:16px 20px; outline:none;
          font-family:Arial,Helvetica,sans-serif; font-size:14px; line-height:1.45; text-align:justify; }
        .ed-caixa .ProseMirror h2 { font-size:16px; font-weight:bold; margin:14px 0 5px; }
        .ed-caixa .ProseMirror h3 { font-size:14px; font-weight:bold; margin:12px 0 4px; }
        .ed-caixa .ProseMirror p { margin:0 0 7px; }
        .ed-caixa .ProseMirror ul, .ed-caixa .ProseMirror ol { margin:0 0 7px 22px; }
        .ed-caixa .ProseMirror img { max-width:100%; height:auto; }
        .ed-caixa .ProseMirror table { border-collapse:collapse; width:100%; margin:10px 0; }
        .ed-caixa .ProseMirror th { background:#0099FF; color:#fff; font-weight:bold;
          border:1px solid #fff; padding:5px 8px; text-align:left; }
        .ed-caixa .ProseMirror td { border:1px solid #ddd; padding:5px 8px; }
        .ed-caixa .ProseMirror .selectedCell { background:#e6f4ff; }
        .ed-caixa .ProseMirror hr.quebra { border:0; border-top:2px dashed #0099FF; margin:14px 0; }
      `}</style>

      <div className="ed-barra">
        <Select {...bt} style={{ width: 130 }} value={
          editor.isActive('heading', { level: 2 }) ? 'h2'
            : editor.isActive('heading', { level: 3 }) ? 'h3' : 'p'
        } onChange={(v) => {
          if (v === 'p') editor.chain().focus().setParagraph().run();
          else editor.chain().focus().toggleHeading({ level: v === 'h2' ? 2 : 3 }).run();
        }} options={[
          { value: 'p', label: 'Texto normal' },
          { value: 'h2', label: 'Título 1' },
          { value: 'h3', label: 'Título 2' }
        ]} />

        <Space.Compact>
          <Button {...bt} type={ativo('bold')} onClick={cmd((c) => c.toggleBold())}><b>N</b></Button>
          <Button {...bt} type={ativo('italic')} onClick={cmd((c) => c.toggleItalic())}><i>I</i></Button>
          <Button {...bt} type={ativo('underline')} onClick={cmd((c) => c.toggleUnderline())}><u>S</u></Button>
        </Space.Compact>

        <Space.Compact>
          <Button {...bt} type={ativo('bulletList')} onClick={cmd((c) => c.toggleBulletList())}>• Lista</Button>
          <Button {...bt} type={ativo('orderedList')} onClick={cmd((c) => c.toggleOrderedList())}>1. Lista</Button>
        </Space.Compact>

        <Space.Compact>
          <Tooltip title="Alinhar à esquerda"><Button {...bt} onClick={cmd((c) => c.setTextAlign('left'))}>⯇</Button></Tooltip>
          <Tooltip title="Centralizar"><Button {...bt} onClick={cmd((c) => c.setTextAlign('center'))}>≡</Button></Tooltip>
          <Tooltip title="Justificar"><Button {...bt} onClick={cmd((c) => c.setTextAlign('justify'))}>▤</Button></Tooltip>
        </Space.Compact>

        <Button {...bt} onClick={cmd((c) => c.insertTable({ rows: 3, cols: 3, withHeaderRow: true }))}>Tabela</Button>
        <Button {...bt} onClick={() => arqRef.current && arqRef.current.click()}>Imagem</Button>

        <Select {...bt} style={{ width: 110 }} placeholder="Símbolo" value={null}
          onChange={(v) => editor.chain().focus().insertContent(v).run()}
          options={SIMBOLOS.map((s) => ({ value: s, label: s }))} />

        <Tooltip title="Força o início de uma página nova no PDF">
          <Button {...bt} onClick={cmd((c) => c.setHorizontalRule())}>Quebra de página</Button>
        </Tooltip>

        <Space.Compact>
          <Button {...bt} onClick={cmd((c) => c.undo())}>↶</Button>
          <Button {...bt} onClick={cmd((c) => c.redo())}>↷</Button>
        </Space.Compact>
      </div>

      {naTabela && (
        <div className="ed-barra" style={{ borderTop: 0, borderRadius: 0, background: '#f0f7ff' }}>
          <Button {...bt} onClick={cmd((c) => c.addRowAfter())}>+ Linha</Button>
          <Button {...bt} onClick={cmd((c) => c.deleteRow())}>− Linha</Button>
          <Button {...bt} onClick={cmd((c) => c.addColumnAfter())}>+ Coluna</Button>
          <Button {...bt} onClick={cmd((c) => c.deleteColumn())}>− Coluna</Button>
          <Button {...bt} onClick={cmd((c) => c.mergeOrSplit())}>Mesclar / dividir</Button>
          <Button {...bt} onClick={cmd((c) => c.toggleHeaderRow())}>Linha de cabeçalho</Button>
          <Button {...bt} danger onClick={cmd((c) => c.deleteTable())}>Excluir tabela</Button>
        </div>
      )}

      <div className="ed-caixa"><EditorContent editor={editor} /></div>
      <input ref={arqRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={escolherImagem} />
    </div>
  );
}
